import React, { useCallback, useMemo, useRef } from 'react';
import { Translate, t } from 'i18n';
import {
  SPlayerContainer,
  SLoadingSpinner,
  SErrorOverlay,
  SErrorMessage,
  SRetryButton
} from './styled';
import { ensureContrast } from './utils/ensureContrast';
import useVideoPlayer from './hooks/useVideoPlayer';
import useControlsVisibility from './hooks/useControlsVisibility';
import useFullscreen from './hooks/useFullscreen';
import useCaptions from './hooks/useCaptions';
import BigPlayButton from './BigPlayButton';
import Controls from './Controls';

interface ICustomVideoPlayerProps {
  url: string;
  subtitlesURL?: string;
  accentColor?: string;
  playing: boolean;
  onPlay: () => void;
  onPause: () => void;
  onEnded: () => void;
  onReady: () => void;
  onError: (error?: MediaError | null) => void;
}

const CustomVideoPlayer: React.FC<ICustomVideoPlayerProps> = ({
  url,
  subtitlesURL,
  accentColor,
  playing,
  onPlay,
  onPause,
  onEnded,
  onReady,
  onError
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const safeAccentColor = useMemo(
    () => ensureContrast(accentColor || '#8dc63f', '#ffffff', 3),
    [accentColor]
  );

  const [playerState, playerActions] = useVideoPlayer(videoRef, {
    src: url,
    hasSubtitles: !!subtitlesURL,
    playing,
    onPlay,
    onPause,
    onEnded,
    onReady,
    onError
  });

  // `crossOrigin="anonymous"` (required for a cross-origin <track>) also puts the *media*
  // fetch into CORS mode, which a CDN allowlist predating `capacitor://localhost` rejects.
  // useVideoPlayer's retry then drops both — captions are the lesser loss.
  const subtitlesEnabled = !!subtitlesURL && !playerState.corsFallbackActive;

  const { visible, onInteraction, onControlsMouseEnter, onControlsMouseLeave } =
    useControlsVisibility(playerState.isPlaying);

  const { isFullscreen, toggleFullscreen } = useFullscreen(containerRef, videoRef);
  const { captionsEnabled, toggleCaptions } = useCaptions(
    videoRef,
    subtitlesEnabled ? subtitlesURL : undefined
  );

  const handleVideoClick = useCallback(() => {
    playerActions.togglePlay();
    onInteraction();
  }, [playerActions, onInteraction]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Don't intercept key events from interactive child elements (buttons, inputs, etc.)
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT') return;

      switch (e.key) {
        case ' ':
        case 'Enter':
          e.preventDefault();
          playerActions.togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          playerActions.seekRelative(-5);
          break;
        case 'ArrowRight':
          e.preventDefault();
          playerActions.seekRelative(5);
          break;
        case 'Escape':
          if (isFullscreen) {
            toggleFullscreen();
          }
          break;
        default:
          break;
      }
      onInteraction();
    },
    [playerActions, isFullscreen, toggleFullscreen, onInteraction]
  );

  const showBigPlayButton = !playerState.hasStarted || playerState.hasEnded;
  const hasError = playerState.error !== null;

  return (
    <SPlayerContainer
      ref={containerRef}
      style={{ '--player-accent-color': safeAccentColor } as React.CSSProperties}
      onMouseMove={onInteraction}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="region"
      aria-label="Video player"
    >
      <video
        ref={videoRef}
        src={url}
        playsInline
        crossOrigin={subtitlesEnabled ? 'anonymous' : undefined}
        onClick={handleVideoClick}
      >
        {subtitlesEnabled && (
          <track kind="subtitles" src={subtitlesURL} srcLang="en" label="Subtitles" />
        )}
      </video>

      {!playerState.isReady && !hasError && <SLoadingSpinner />}

      {showBigPlayButton && !hasError && (
        <BigPlayButton accentColor={safeAccentColor} onClick={playerActions.togglePlay} />
      )}

      {hasError && (
        <SErrorOverlay>
          <SErrorMessage>
            <Translate str="frontend.media_player.video_player.errors.something_went_wrong" />
          </SErrorMessage>
          {/*
           * No retry button once a retry cannot help (a decode failure, or the CORS
           * fallback already spent): offering one would be an infinite no-op.
           */}
          {playerState.canRetry && (
            <SRetryButton
              type="button"
              onClick={playerActions.retry}
              aria-label={t('frontend.media_player.audio_player.aria_reload_button')}
              title={t('frontend.media_player.audio_player.aria_reload_button')}
            >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
              </svg>
            </SRetryButton>
          )}
        </SErrorOverlay>
      )}

      <Controls
        visible={visible}
        isPlaying={playerState.isPlaying}
        currentTime={playerState.currentTime}
        duration={playerState.duration}
        buffered={playerState.buffered}
        playbackRate={playerState.playbackRate}
        captionsEnabled={captionsEnabled}
        hasSubtitles={subtitlesEnabled}
        isFullscreen={isFullscreen}
        videoRef={videoRef}
        onTogglePlay={playerActions.togglePlay}
        onSeek={playerActions.seek}
        onChangeRate={playerActions.setPlaybackRate}
        onToggleCaptions={toggleCaptions}
        onToggleFullscreen={toggleFullscreen}
        onMouseEnter={onControlsMouseEnter}
        onMouseLeave={onControlsMouseLeave}
      />
    </SPlayerContainer>
  );
};

// NOTE: We do NOT wrap with withVideoErrorBoundary here.
// That HOC strips onVideoPlayerError from props before passing them to the wrapped component,
// which would prevent native <video> error events from propagating back to VideoContent.
// Instead, <video> errors are handled directly via the onError callback in useVideoPlayer,
// and React rendering errors are caught by the existing error boundary higher in the tree.
export default CustomVideoPlayer;
